import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { doc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from '../firebase.js';
import { authService, calculateUserPermissions } from '../services/authService.ts';
import userActivityLogService from '../services/userActivityLogService.ts';
import {
  User,
  AuthState,
  LoginCredentials,
  RegistrationData,
  Permission,
  FeatureName,
  FEATURE_PERMISSIONS
} from '../types/auth.ts';
import { AppError, ErrorCodes } from '../utils/errorHandler.ts';
import { logger } from '../utils/logger.ts';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: RegistrationData) => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  updateUserProfile: (updates: Partial<User>) => Promise<void>;
  updateUserPhoneNumber: (phoneNumber: string) => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  isAdmin: boolean;
  isManager: boolean;
  isViewer: boolean;
  isEmployee: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new AppError(
      'useAuth must be used within an AuthProvider',
      ErrorCodes.VALIDATION_ERROR,
      { hook: 'useAuth' }
    );
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null
  });

  // 🔒 P1 보안 개선: 실시간 권한 동기화를 위한 ref
  const permissionSyncUnsubscribeRef = useRef<Unsubscribe | null>(null);

  // Subscribe to auth state changes
  useEffect(() => {
    logger.debug('[AuthContext] Setting up auth state listener');
    const unsubscribe = authService.onAuthStateChanged((user) => {
      logger.debug('[AuthContext] Auth state changed:', {
        context: {
          hasUser: !!user,
          userEmail: user?.email,
          userRole: user?.role
        }
      });
      setState({
        user,
        loading: false,
        error: null
      });
    });

    return () => {
      logger.debug('[AuthContext] Cleaning up auth state listener');
      unsubscribe();
    };
  }, []);

  // 🔒 P1 보안 개선: 실시간 권한 동기화 (onSnapshot)
  // 관리자가 권한을 변경하면 재로그인 없이 즉시 반영
  useEffect(() => {
    // 기존 리스너 정리
    if (permissionSyncUnsubscribeRef.current) {
      permissionSyncUnsubscribeRef.current();
      permissionSyncUnsubscribeRef.current = null;
    }

    // 사용자가 로그인되어 있을 때만 실시간 동기화 설정
    if (state.user?.uid) {
      logger.debug('[AuthContext] Setting up real-time permission sync for:', {
        context: { uid: state.user.uid }
      });

      const userDocRef = doc(db, 'users', state.user.uid);

      permissionSyncUnsubscribeRef.current = onSnapshot(
        userDocRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            const newPermissions = calculateUserPermissions(data);
            const newRole = data.role;

            // 🔧 FIX: setState의 prev를 사용하여 stale closure 문제 해결
            // 이전: state.user?.permissions를 직접 참조 (stale closure)
            // 이후: prev.user를 통해 최신 상태와 비교
            setState(prev => {
              if (!prev.user) return prev;

              const currentPermissionsStr = JSON.stringify([...(prev.user.permissions || [])].sort());
              const newPermissionsStr = JSON.stringify([...newPermissions].sort());
              const roleChanged = prev.user.role !== newRole;

              if (currentPermissionsStr !== newPermissionsStr || roleChanged) {
                logger.debug('[AuthContext] User data changed, updating state:', {
                  context: {
                    oldRole: prev.user.role,
                    newRole,
                    roleChanged,
                    oldPermissions: prev.user.permissions,
                    newPermissions
                  }
                });

                return {
                  ...prev,
                  user: {
                    ...prev.user,
                    role: newRole,
                    permissions: newPermissions,
                    customPermissions: data.customPermissions || [],
                    revokedPermissions: data.revokedPermissions || []
                  }
                };
              }

              return prev;
            });
          }
        },
        (error) => {
          logger.error('[AuthContext] Real-time permission sync error:', error);
        }
      );
    }

    return () => {
      if (permissionSyncUnsubscribeRef.current) {
        logger.debug('[AuthContext] Cleaning up real-time permission sync');
        permissionSyncUnsubscribeRef.current();
        permissionSyncUnsubscribeRef.current = null;
      }
    };
  }, [state.user?.uid]); // uid가 변경될 때만 리스너 재설정

  // Login function
  const login = async (credentials: LoginCredentials): Promise<void> => {
    logger.debug('[AuthContext] Login attempt started');
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const user = await authService.login(credentials);
      logger.debug('[AuthContext] Login successful, updating state');
      setState({
        user,
        loading: false,
        error: null
      });

      // 로그인 활동 로그 기록
      try {
        await userActivityLogService.logLogin(
          user.uid,
          user.email,
          user.displayName
        );
        logger.debug('[AuthContext] Login activity logged successfully');
      } catch (logError) {
        logger.error('[AuthContext] Failed to log login activity:', logError instanceof Error ? logError : undefined);
        // 로그 기록 실패는 로그인 프로세스를 중단하지 않음
      }
    } catch (error: any) {
      logger.error('[AuthContext] Login failed:', error instanceof Error ? error : undefined);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
      throw error;
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    // 로그아웃 전에 현재 사용자 정보 저장 (로그 기록용)
    const currentUser = state.user;
    
    try {
      // 로그아웃 활동 로그 기록 (로그아웃 전에 기록)
      if (currentUser) {
        try {
          await userActivityLogService.logLogout(
            currentUser.uid,
            currentUser.email,
            currentUser.displayName
          );
          logger.debug('[AuthContext] Logout activity logged successfully');
        } catch (logError) {
          logger.error('[AuthContext] Failed to log logout activity:', logError instanceof Error ? logError : undefined);
          // 로그 기록 실패는 로그아웃 프로세스를 중단하지 않음
        }
      }
      
      await authService.logout();
      setState({
        user: null,
        loading: false,
        error: null
      });
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
      throw error;
    }
  };

  // Register function
  const register = async (data: RegistrationData): Promise<void> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const user = await authService.register(data);
      setState({
        user,
        loading: false,
        error: null
      });
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
      throw error;
    }
  };

  // Send password reset email function
  const sendPasswordResetEmail = async (email: string): Promise<void> => {
    logger.debug('[AuthContext] Password reset email request started for:', { context: { email } });
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      await authService.sendPasswordResetEmail(email);
      logger.debug('[AuthContext] Password reset email sent successfully');
      setState(prev => ({ ...prev, loading: false, error: null }));
    } catch (error: any) {
      logger.error('[AuthContext] Password reset email failed:', error instanceof Error ? error : undefined);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
      throw error;
    }
  };

  // Permission checking functions
  const hasPermission = (permission: Permission): boolean => {
    return authService.hasPermission(state.user, permission);
  };

  const hasAnyPermission = (permissions: Permission[]): boolean => {
    return authService.hasAnyPermission(state.user, permissions);
  };

  const hasAllPermissions = (permissions: Permission[]): boolean => {
    return authService.hasAllPermissions(state.user, permissions);
  };

  // Update user profile function
  const updateUserProfile = async (updates: Partial<User>): Promise<void> => {
    if (!state.user) {
      throw new AppError(
        '로그인된 사용자가 없습니다.',
        ErrorCodes.AUTH_ERROR,
        { function: 'updateUserProfile' },
        true
      );
    }

    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const updatedUser = await authService.updateUserProfile(state.user.uid, updates);
      setState(prev => ({
        ...prev,
        user: updatedUser,
        loading: false,
        error: null
      }));
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
      throw error;
    }
  };

  // Update user phone number function
  const updateUserPhoneNumber = async (phoneNumber: string): Promise<void> => {
    if (!state.user) {
      throw new AppError(
        '로그인된 사용자가 없습니다.',
        ErrorCodes.AUTH_ERROR,
        { function: 'updateUserPhoneNumber' },
        true
      );
    }

    logger.debug('[AuthContext] updateUserPhoneNumber called', { context: { phoneNumber } });
    logger.debug('[AuthContext] Current user before update', { context: { user: state.user } });

    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const updatedUser = await authService.updateUserPhoneNumber(state.user.uid, phoneNumber);
      logger.debug('[AuthContext] Updated user from authService', { context: { updatedUser } });

      setState(prev => ({
        ...prev,
        user: updatedUser,
        loading: false,
        error: null
      }));

      logger.debug('[AuthContext] State updated successfully');
    } catch (error: any) {
      logger.error('[AuthContext] Error updating phone number:', error instanceof Error ? error : undefined);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
      throw error;
    }
  };

  // Role checking helpers
  const isAdmin = state.user?.role === 'admin';
  const isManager = state.user?.role === 'manager';
  const isViewer = state.user?.role === 'viewer';
  const isEmployee = state.user?.role === 'employee';

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    register,
    sendPasswordResetEmail,
    updateUserProfile,
    updateUserPhoneNumber,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    isManager,
    isViewer,
    isEmployee
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hooks for common permission checks
export const usePermission = (permission: Permission): boolean => {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
};

export const useAnyPermission = (permissions: Permission[]): boolean => {
  const { hasAnyPermission } = useAuth();
  return hasAnyPermission(permissions);
};

export const useAllPermissions = (permissions: Permission[]): boolean => {
  const { hasAllPermissions } = useAuth();
  return hasAllPermissions(permissions);
};

/**
 * 🔒 P2 보안 개선: 타입 안전한 편집 권한 확인 훅
 *
 * @param feature - FeatureName 타입으로 제한된 기능명
 * @returns 해당 기능의 edit 권한 보유 여부
 *
 * @example
 * ```tsx
 * const canEditTeams = useCanEdit('teams');  // ✅ 타입 안전
 * const canEditFoo = useCanEdit('foo');      // ❌ 컴파일 에러
 * ```
 */
export const useCanEdit = (feature: FeatureName): boolean => {
  const { hasPermission } = useAuth();
  const featurePermissions = FEATURE_PERMISSIONS[feature];
  return hasPermission(featurePermissions.edit);
};

/**
 * 🔒 P2 보안 개선: 타입 안전한 조회 권한 확인 훅
 *
 * @param feature - FeatureName 타입으로 제한된 기능명
 * @returns 해당 기능의 view 권한 보유 여부
 *
 * @example
 * ```tsx
 * const canViewPayroll = useCanView('payroll');  // ✅ 타입 안전
 * const canViewBar = useCanView('bar');          // ❌ 컴파일 에러
 * ```
 */
export const useCanView = (feature: FeatureName): boolean => {
  const { hasPermission } = useAuth();
  const featurePermissions = FEATURE_PERMISSIONS[feature];
  return hasPermission(featurePermissions.view);
};